import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";
import type { BillingModel, CatalogueItem, CostPlan, ElevenLabsBalance, FxRate, PnlRollup } from "../api/types";
import { useMe } from "../lib/me";
import { toast } from "../components/toast";

const REPORTING_CCYS = ["GBP", "USD", "EUR"];

// --- formatting helpers (currency-aware) ------------------------------------

function money(n: number | null | undefined, ccy = "USD"): string {
  if (n == null) return "—";
  try {
    return n.toLocaleString("en-GB", { style: "currency", currency: ccy, maximumFractionDigits: 2 });
  } catch {
    return `${ccy} ${n.toFixed(2)}`;
  }
}
/** A small per-unit rate needs more precision than 2dp. */
function rateOf(n: number | null | undefined, ccy = "USD"): string {
  if (n == null) return "—";
  try {
    return n.toLocaleString("en-GB", { style: "currency", currency: ccy, minimumFractionDigits: 2, maximumFractionDigits: 6 });
  } catch {
    return `${ccy} ${n.toFixed(6)}`;
  }
}
function hours(seconds: number): string {
  return (seconds / 3600).toLocaleString("en-GB", { maximumFractionDigits: 1 }) + " h";
}
function pct(n: number | null | undefined): string {
  return n == null ? "—" : Math.round(n * 100) + "%";
}
function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// --- P&L panel --------------------------------------------------------------

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="cost-stat">
      <div className="cost-stat-label">{label}</div>
      <div className="cost-stat-value">{value}</div>
      {sub && <div className="cost-stat-sub">{sub}</div>}
    </div>
  );
}

function UtilBadge({ u }: { u: number | null }) {
  if (u == null) return <span className="muted">—</span>;
  const cls = u > 1 ? "over" : u >= 0.85 ? "high" : u < 0.4 ? "low" : "ok";
  return <span className={`cost-util cost-util-${cls}`}>{pct(u)}</span>;
}

function PnlPanel({ enabled }: { enabled: boolean }) {
  const [month, setMonth] = useState(currentMonth());
  const [ccy, setCcy] = useState("GBP");
  const { data, isLoading } = useQuery({
    queryKey: ["pnl", month, ccy],
    queryFn: () => api<PnlRollup>(`/api/admin/pnl?month=${month}&currency=${ccy}`),
    enabled,
  });

  const r = data?.reporting_currency || ccy;
  const providers = data ? Object.entries(data.attributed.by_provider) : [];
  const ac = data?.actual_cash;
  const unconverted = data?.fx?.unconverted ?? [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="cost-panel-head">
        <h3 style={{ margin: 0 }}>Monthly P&amp;L — cost side</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <select className="input" value={ccy} onChange={(e) => setCcy(e.target.value)} style={{ width: 84 }}
            title="Reporting currency">
            {REPORTING_CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input" type="month" value={month}
            onChange={(e) => setMonth(e.target.value || currentMonth())} style={{ width: 150 }} />
        </div>
      </div>

      {isLoading && !data ? (
        <div className="muted">Loading…</div>
      ) : !data ? (
        <div className="muted">No data.</div>
      ) : (
        <>
          <div className="cost-stats">
            <Stat label={`Actual cash (COGS) · ${r}`} value={money(ac!.total, r)}
              sub={`${money(ac!.variable, r)} usage · ${money(ac!.subscriptions, r)} subs · ${money(ac!.overhead, r)} overhead`} />
            <Stat label={`Attributed to jobs · ${r}`} value={money(data.attributed.total, r)}
              sub={`${data.attributed.events} transaction(s) — the per-book / pricing view`} />
          </div>

          {unconverted.length > 0 && (
            <div className="cost-warn">
              {unconverted.length} amount(s) couldn't be converted to {r} — no exchange rate on file for{" "}
              {[...new Set(unconverted.map((u) => u.currency))].join(", ")}. Add the rate(s) below; they're excluded from the totals above.
            </div>
          )}

          <h4 className="cost-sub">Attributed by provider</h4>
          {providers.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>No usage recorded this month.</div>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Provider</th><th className="r">Cost</th><th className="r">Transactions</th><th className="r">Audio</th></tr>
              </thead>
              <tbody>
                {providers.sort((a, b) => b[1].cost - a[1].cost).map(([prov, v]) => (
                  <tr key={prov}>
                    <td>{prov}</td>
                    <td className="r">{money(v.cost, r)}</td>
                    <td className="r">{v.events}</td>
                    <td className="r muted">{v.audio_seconds > 0 ? hours(v.audio_seconds) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.subscriptions.length > 0 && (
            <>
              <h4 className="cost-sub">Subscriptions</h4>
              <table className="table">
                <thead>
                  <tr>
                    <th>Plan</th><th className="r">Used / quota</th><th className="r">Util.</th>
                    <th className="r">Cost / min</th><th className="r">Overage</th><th className="r">Cash</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscriptions.map((s) => (
                    <tr key={s.plan_id}>
                      <td>{s.label}<span className="muted"> · {s.provider} · {s.native_currency}</span></td>
                      <td className="r">
                        {s.units_used == null ? "—" : `${s.units_used.toLocaleString()} / ${s.included_units?.toLocaleString() ?? "—"}`}
                        {s.unit_type && <span className="muted"> {s.unit_type.replace("_", " ")}</span>}
                      </td>
                      <td className="r"><UtilBadge u={s.utilization} /></td>
                      <td className="r muted" title={`${rateOf(s.implied_unit_rate, r)} / ${(s.unit_type || "unit").replace("_", " ")}`}>
                        {s.cost_per_audio_minute == null ? "—" : `${rateOf(s.cost_per_audio_minute, r)}/min`}
                      </td>
                      <td className="r">{s.overage_units > 0 ? s.overage_units.toLocaleString() : "—"}</td>
                      <td className="r">{money(s.monthly_cash, r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {data.overhead.length > 0 && (
            <>
              <h4 className="cost-sub">Overhead</h4>
              <table className="table">
                <thead><tr><th>Item</th><th className="r">Monthly</th></tr></thead>
                <tbody>
                  {data.overhead.map((o) => (
                    <tr key={o.plan_id}>
                      <td>{o.label}<span className="muted"> · {o.provider} · {o.native_currency}</span></td>
                      <td className="r">{money(o.monthly_cash, r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {Object.keys(data.fx.rates_used).length > 0 && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
              Rates used: {Object.entries(data.fx.rates_used).map(([k, v]) => `${k} = ${v}`).join(" · ")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- FX rates manager -------------------------------------------------------

function FxRatesPanel({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const [f, setF] = useState({ base: "USD", quote: "GBP", date: "", rate: "" });
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const { data } = useQuery({
    queryKey: ["fx-rates"],
    queryFn: () => api<{ rates: FxRate[] }>("/api/admin/fx-rates"),
    enabled,
  });

  const fetchRate = useMutation({
    mutationFn: () => {
      const qs = new URLSearchParams({ base: f.base.trim(), quote: f.quote.trim(), date: f.date });
      return api(`/api/admin/fx-rates/fetch?${qs.toString()}`, { method: "POST" });
    },
    onSuccess: (r: any) => { toast(`Fetched ${r.base}→${r.quote} = ${r.rate} (${r.date})`, "success"); onChanged(); },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't fetch the rate.", "error"),
  });
  const saveManual = useMutation({
    mutationFn: () => api("/api/admin/fx-rates", {
      method: "POST",
      body: JSON.stringify({ base: f.base.trim(), quote: f.quote.trim(), date: f.date, rate: Number(f.rate) }),
    }),
    onSuccess: () => { toast("Rate saved.", "success"); onChanged(); },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't save the rate.", "error"),
  });

  const rates = data?.rates ?? [];

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h3 style={{ margin: "0 0 4px" }}>Exchange rates</h3>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
        Rates are the ECB mid-market rate (what Wise quotes) for the date a payment is made. Fetch one automatically,
        or pin an exact rate by hand. Conversions use the rate on-or-before the relevant date.
      </div>
      <div className="cost-fx-form">
        <input className="input" placeholder="USD" value={f.base} onChange={(e) => set("base", e.target.value.toUpperCase())} style={{ width: 72 }} />
        <span className="muted">→</span>
        <input className="input" placeholder="GBP" value={f.quote} onChange={(e) => set("quote", e.target.value.toUpperCase())} style={{ width: 72 }} />
        <input className="input" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} style={{ width: 150 }} />
        <button className="btn sm" disabled={!f.date || fetchRate.isPending} onClick={() => fetchRate.mutate()}>
          {fetchRate.isPending ? "Fetching…" : "Fetch"}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>or pin</span>
        <input className="input mono" placeholder="rate" value={f.rate} onChange={(e) => set("rate", e.target.value)} style={{ width: 90 }} />
        <button className="btn ghost sm" disabled={!f.date || !f.rate || saveManual.isPending} onClick={() => saveManual.mutate()}>Save</button>
      </div>

      {rates.length > 0 && (
        <table className="table" style={{ marginTop: 14 }}>
          <thead><tr><th>Pair</th><th>Date</th><th className="r">Rate</th><th>Source</th></tr></thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id}>
                <td>{r.base} → {r.quote}</td>
                <td className="muted">{r.date}</td>
                <td className="r mono">{r.rate}</td>
                <td><span className="muted" style={{ fontSize: 12 }}>{r.source}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- ElevenLabs balance reconciliation --------------------------------------

function ElevenLabsBalanceCard({ enabled }: { enabled: boolean }) {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["el-balance"],
    queryFn: () => api<ElevenLabsBalance>("/api/admin/providers/elevenlabs/balance"),
    enabled,
  });
  if (!data) return null;
  const rep = data.reported;
  const est = data.estimate;
  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="cost-panel-head">
        <h3 style={{ margin: 0 }}>ElevenLabs balance — double-check</h3>
        <button className="btn ghost sm" disabled={isFetching} onClick={() => refetch()}>
          {isFetching ? "…" : "Refresh"}
        </button>
      </div>
      <div className="cost-stats">
        <Stat
          label="ElevenLabs reports (this cycle)"
          value={rep && rep.used_credits != null
            ? `${rep.used_credits.toLocaleString()} / ${rep.limit_credits?.toLocaleString() ?? "—"} credits`
            : "—"}
          sub={rep
            ? `${rep.remaining_credits?.toLocaleString() ?? "—"} remaining${rep.tier ? ` · ${rep.tier}` : ""}`
            : (data.error || "unavailable")}
        />
        <Stat
          label="Mockingbird estimate (this month)"
          value={`${est.credits.toLocaleString()} credits`}
          sub={`${est.audio_minutes.toLocaleString()} audio min · ${est.events} task(s) since ${est.period_start}`}
        />
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
        ElevenLabs' counter is per its billing cycle; the estimate is this calendar month, so expect a small offset.
      </div>
    </div>
  );
}

// --- Cost-plan editor -------------------------------------------------------

const BLANK = {
  provider: "",
  service_key: "",
  billing_model: "subscription" as BillingModel,
  label: "",
  currency: "USD",
  unit_type: "audio_minute",
  fixed_fee: "",
  period: "month",
  included_units: "",
  overage_rate: "",
  units_per_audio_minute: "",
  rate_json: '{"per_unit": 0}',
  effective_from: "",
};

const catalogueKey = (c: { provider: string; service_key: string }) => `${c.provider} ${c.service_key}`;

function NewPlanForm({ catalogue, preset, onCreated }: {
  catalogue: CatalogueItem[];
  preset: CatalogueItem | null;
  onCreated: () => void;
}) {
  const [f, setF] = useState({ ...BLANK });
  const [sel, setSel] = useState<string>("");  // catalogue key, "" = none yet, "custom" = free text
  const set = (k: keyof typeof BLANK, v: string) => setF((s) => ({ ...s, [k]: v }));

  const applyItem = (c: CatalogueItem) => {
    setSel(catalogueKey(c));
    setF((s) => ({
      ...s,
      provider: c.provider,
      service_key: c.service_key,
      billing_model: c.billing_model,
      unit_type: c.unit_type ?? "",
      currency: c.currency,
      period: c.period,
      label: s.label || c.label,
    }));
  };
  // Picking a not-set centre in the coverage panel prefills the form here.
  useEffect(() => {
    if (preset) applyItem(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const onSelectCentre = (v: string) => {
    if (v === "custom") {
      setSel("custom");
      setF((s) => ({ ...s, provider: "", service_key: "" }));
      return;
    }
    const c = catalogue.find((x) => catalogueKey(x) === v);
    if (c) applyItem(c);
    else { setSel(""); setF((s) => ({ ...s, provider: "", service_key: "" })); }
  };

  const isCustom = sel === "custom";
  const picked = !isCustom && sel ? catalogue.find((x) => catalogueKey(x) === sel) ?? null : null;

  const create = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        provider: f.provider.trim(),
        service_key: f.service_key.trim(),
        billing_model: f.billing_model,
        label: f.label.trim(),
        currency: f.currency.trim().toUpperCase() || "USD",
        period: f.period,
        effective_from: f.effective_from || undefined,
      };
      if (f.billing_model === "subscription") {
        body.unit_type = f.unit_type.trim() || null;
        body.fixed_fee = Number(f.fixed_fee) || 0;
        body.included_units = f.included_units ? Number(f.included_units) : null;
        body.overage_rate = f.overage_rate ? Number(f.overage_rate) : null;
        body.units_per_audio_minute = f.units_per_audio_minute ? Number(f.units_per_audio_minute) : null;
      } else if (f.billing_model === "fixed") {
        body.fixed_fee = Number(f.fixed_fee) || 0;
      } else {
        body.unit_type = f.unit_type.trim() || null;
        body.rate_json = f.rate_json || "{}";
      }
      return api<CostPlan>("/api/admin/cost-plans", { method: "POST", body: JSON.stringify(body) });
    },
    onSuccess: () => {
      toast("Cost plan added.", "success");
      setF({ ...BLANK });
      onCreated();
    },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't add the plan.", "error"),
  });

  const m = f.billing_model;
  const ccy = f.currency.trim().toUpperCase() || "USD";
  const perAudioMin = f.unit_type.trim() === "audio_minute" || !f.unit_type.trim()
    ? 1
    : (f.units_per_audio_minute ? Number(f.units_per_audio_minute) : null);
  const implied =
    m === "subscription" && Number(f.fixed_fee) > 0 && Number(f.included_units) > 0
      ? Number(f.fixed_fee) / Number(f.included_units)
      : null;
  const perMin = implied != null && perAudioMin != null ? implied * perAudioMin : null;

  return (
    <div className="card cost-form" id="cost-add-form">
      <h3 style={{ margin: "0 0 4px" }}>Add a cost plan</h3>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
        Adding a plan for a provider + service you already have closes the prior one from this date — that's how a
        tier switch is recorded. Jobs are priced at the plan in force when they ran.
      </div>

      <div className="cost-grid">
        <label className="field cost-grid-wide">
          <span>Cost centre</span>
          <select className="input" value={sel} onChange={(e) => onSelectCentre(e.target.value)}>
            <option value="">Choose a service…</option>
            <optgroup label="Metered APIs / compute">
              {catalogue.filter((c) => c.category === "api" || c.metered).map((c) => (
                <option key={catalogueKey(c)} value={catalogueKey(c)}>
                  {c.label}{c.has_plan ? "  ✓ covered" : ""}
                </option>
              ))}
            </optgroup>
            <optgroup label="Infrastructure / overhead">
              {catalogue.filter((c) => c.category !== "api" && !c.metered).map((c) => (
                <option key={catalogueKey(c)} value={catalogueKey(c)}>
                  {c.label}{c.has_plan ? "  ✓ covered" : ""}
                </option>
              ))}
            </optgroup>
            <option value="custom">Custom (type manually)…</option>
          </select>
          {picked && <em className="muted" style={{ fontSize: 11.5 }}>{picked.hint}</em>}
        </label>

        {isCustom ? (
          <>
            <label className="field">
              <span>Provider</span>
              <input className="input" value={f.provider} placeholder="elevenlabs / anthropic / render"
                onChange={(e) => set("provider", e.target.value)} />
            </label>
            <label className="field">
              <span>Service key {m !== "fixed" && <em className="muted">(blank = all)</em>}</span>
              <input className="input" value={f.service_key}
                placeholder={m === "usage" ? "claude-opus-4-8" : m === "fixed" ? "hosting" : "transcription"}
                onChange={(e) => set("service_key", e.target.value)} />
            </label>
          </>
        ) : picked ? (
          <label className="field">
            <span>Provider · service</span>
            <div className="input" style={{ display: "flex", alignItems: "center", background: "transparent" }}>
              <span className="mono">{f.provider}{f.service_key && ` / ${f.service_key}`}</span>
            </div>
          </label>
        ) : null}

        <label className="field">
          <span>Billing model</span>
          <select className="input" value={f.billing_model} onChange={(e) => set("billing_model", e.target.value)}>
            <option value="subscription">Subscription (fee + quota)</option>
            <option value="usage">Usage (pay-as-you-go)</option>
            <option value="fixed">Fixed overhead</option>
          </select>
        </label>
        <label className="field">
          <span>Label</span>
          <input className="input" value={f.label} placeholder="ElevenLabs Pro"
            onChange={(e) => set("label", e.target.value)} />
        </label>
        <label className="field">
          <span>Currency</span>
          <input className="input" value={f.currency} placeholder="USD"
            onChange={(e) => set("currency", e.target.value.toUpperCase())} />
        </label>

        {m === "subscription" && (
          <>
            <label className="field">
              <span>Fixed fee</span>
              <input className="input" type="number" step="0.01" value={f.fixed_fee} placeholder="99"
                onChange={(e) => set("fixed_fee", e.target.value)} />
            </label>
            <label className="field">
              <span>Included quota</span>
              <input className="input" type="number" step="any" value={f.included_units} placeholder="4000"
                onChange={(e) => set("included_units", e.target.value)} />
            </label>
            <label className="field">
              <span>Unit type</span>
              <input className="input" value={f.unit_type} placeholder="audio_minute"
                onChange={(e) => set("unit_type", e.target.value)} />
            </label>
            {f.unit_type.trim() && f.unit_type.trim() !== "audio_minute" && (
              <label className="field">
                <span>Units per audio minute</span>
                <input className="input" type="number" step="any" value={f.units_per_audio_minute} placeholder="≈ 20.16 for Scribe"
                  onChange={(e) => set("units_per_audio_minute", e.target.value)} />
                <em className="muted" style={{ fontSize: 11.5 }}>
                  How many {f.unit_type.trim()}s one audio minute uses (ElevenLabs Scribe ≈ 20.16 credits/min).
                </em>
              </label>
            )}
            <label className="field">
              <span>Overage rate <em className="muted">(per unit, optional)</em></span>
              <input className="input" type="number" step="any" value={f.overage_rate} placeholder="implied"
                onChange={(e) => set("overage_rate", e.target.value)} />
            </label>
          </>
        )}

        {m === "fixed" && (
          <label className="field">
            <span>Monthly fee</span>
            <input className="input" type="number" step="0.01" value={f.fixed_fee} placeholder="25"
              onChange={(e) => set("fixed_fee", e.target.value)} />
          </label>
        )}

        {m === "usage" && (
          <>
            <label className="field">
              <span>Unit type</span>
              <input className="input" value={f.unit_type} placeholder="input_token"
                onChange={(e) => set("unit_type", e.target.value)} />
            </label>
            <label className="field cost-grid-wide">
              <span>Rate JSON</span>
              <input className="input mono" value={f.rate_json}
                onChange={(e) => set("rate_json", e.target.value)} />
              <em className="muted" style={{ fontSize: 11.5 }}>
                {`{"per_unit": 0.02} — or Anthropic per-1M: {"input":5,"output":25,"cache_read":0.5,"cache_write":6.25}`}
              </em>
            </label>
          </>
        )}

        <label className="field">
          <span>Effective from <em className="muted">(blank = today)</em></span>
          <input className="input" type="date" value={f.effective_from}
            onChange={(e) => set("effective_from", e.target.value)} />
        </label>
      </div>

      <div className="cost-form-foot">
        {implied != null && (
          <span className="muted">
            Implied: <strong>{rateOf(implied, ccy)}</strong> / {f.unit_type || "unit"}
            {perMin != null && <> · <strong>{rateOf(perMin, ccy)}</strong> / audio min</>}
          </span>
        )}
        <button className="btn primary" disabled={!f.provider.trim() || create.isPending}
          onClick={() => create.mutate()}>
          {create.isPending ? "Adding…" : "Add plan"}
        </button>
      </div>
    </div>
  );
}

function PlanRow({ p, onChanged }: { p: CostPlan; onChanged: () => void }) {
  const close = useMutation({
    mutationFn: () =>
      api(`/api/admin/cost-plans/${p.id}`, {
        method: "POST",
        body: JSON.stringify({ active: false, effective_to: new Date().toISOString().slice(0, 10) }),
      }),
    onSuccess: () => { toast("Plan deactivated.", "success"); onChanged(); },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't update the plan.", "error"),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/admin/cost-plans/${p.id}`, { method: "DELETE" }),
    onSuccess: () => { toast("Plan deleted.", "success"); onChanged(); },
    onError: (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't delete the plan.", "error"),
  });

  const c = p.currency || "USD";
  // For a non-audio-minute subscription, show the effective $/audio-min too.
  const perMin = p.billing_model === "subscription" && p.implied_unit_rate != null
    ? p.implied_unit_rate * (p.unit_type === "audio_minute" || !p.unit_type ? 1 : (p.units_per_audio_minute ?? NaN))
    : null;
  const basis =
    p.billing_model === "subscription"
      ? `${money(p.fixed_fee, c)}/${p.period} · ${p.included_units?.toLocaleString() ?? "—"} ${p.unit_type ?? ""} → ${rateOf(p.implied_unit_rate, c)}/unit`
        + (perMin != null && !Number.isNaN(perMin) ? ` · ${rateOf(perMin, c)}/min` : "")
      : p.billing_model === "fixed"
        ? `${money(p.fixed_fee, c)}/${p.period}`
        : `usage · ${p.rate_json}`;

  return (
    <tr className={p.active ? "" : "cost-inactive"}>
      <td><span className={`cost-bm cost-bm-${p.billing_model}`}>{p.billing_model}</span></td>
      <td>{p.provider}{p.service_key && <span className="muted"> / {p.service_key}</span>}</td>
      <td>{p.label || <span className="muted">—</span>}<span className="muted"> · {c}</span></td>
      <td className="mono" style={{ fontSize: 12 }}>{basis}</td>
      <td className="muted" style={{ fontSize: 12 }}>{p.effective_from ?? "—"} → {p.effective_to ?? "open"}</td>
      <td className="r">
        {p.active && (
          <button className="btn ghost sm" disabled={close.isPending} onClick={() => close.mutate()}
            title="Stop using this plan from today">Deactivate</button>
        )}
        <button className="btn ghost sm" disabled={del.isPending}
          onClick={() => { if (confirm(`Delete plan "${p.label || p.provider}"? This removes it from history.`)) del.mutate(); }}
          title="Delete permanently">✕</button>
      </td>
    </tr>
  );
}

function CoveragePanel({ catalogue, onPick }: { catalogue: CatalogueItem[]; onPick: (c: CatalogueItem) => void }) {
  if (!catalogue.length) return null;
  const covered = catalogue.filter((c) => c.has_plan).length;
  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h3 style={{ margin: "0 0 4px" }}>What we pay for</h3>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Every cost centre the app relies on — {covered}/{catalogue.length} have a plan. Add a missing one and the
        provider/service keys are filled in for you (no spelling to get wrong).
      </div>
      <table className="table">
        <thead>
          <tr><th>Service</th><th>Keys</th><th>Kind</th><th className="r">Status</th></tr>
        </thead>
        <tbody>
          {catalogue.map((c) => (
            <tr key={catalogueKey(c)} className={c.has_plan ? "" : "cost-inactive"}>
              <td>{c.label}</td>
              <td className="mono" style={{ fontSize: 12 }}>{c.provider}{c.service_key && ` / ${c.service_key}`}</td>
              <td className="muted" style={{ fontSize: 12 }}>{c.metered ? "metered" : c.category === "api" ? "API" : "overhead"}</td>
              <td className="r">
                {c.has_plan
                  ? <span className="muted" style={{ fontSize: 12 }}>✓ covered{c.plan_label ? ` · ${c.plan_label}` : ""}</span>
                  : <button className="btn ghost sm" onClick={() => onPick(c)}>Add plan</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminCosts() {
  const { data: me, isLoading: meLoading } = useMe();
  const qc = useQueryClient();
  const enabled = !!me?.is_superadmin;
  const [preset, setPreset] = useState<CatalogueItem | null>(null);

  const plansQ = useQuery({
    queryKey: ["cost-plans"],
    queryFn: () => api<{ plans: CostPlan[] }>("/api/admin/cost-plans"),
    enabled,
  });
  const catalogueQ = useQuery({
    queryKey: ["cost-catalogue"],
    queryFn: () => api<{ catalogue: CatalogueItem[] }>("/api/admin/cost-catalogue"),
    enabled,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["cost-plans"] });
    qc.invalidateQueries({ queryKey: ["cost-catalogue"] });
    qc.invalidateQueries({ queryKey: ["pnl"] });
    qc.invalidateQueries({ queryKey: ["fx-rates"] });
  };

  const catalogue = catalogueQ.data?.catalogue ?? [];
  const pickToAdd = (c: CatalogueItem) => {
    setPreset({ ...c });  // fresh ref so re-picking the same centre re-applies
    document.getElementById("cost-add-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const plans = plansQ.data?.plans ?? [];
  const grouped = useMemo(() => ({
    active: plans.filter((p) => p.active),
    history: plans.filter((p) => !p.active),
  }), [plans]);

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (!me?.is_superadmin) return <Navigate to="/" replace />;

  return (
    <div className="page admin-page">
      <div className="page-head">
        <div>
          <h1>Costs &amp; P&amp;L</h1>
          <div className="sub">
            Enter your service plans + exchange rates, then read the monthly cost roll-up in any currency.
            <span className="muted"> · superadmin</span>
          </div>
        </div>
      </div>

      <PnlPanel enabled={enabled} />
      <CoveragePanel catalogue={catalogue} onPick={pickToAdd} />
      <NewPlanForm catalogue={catalogue} preset={preset} onCreated={refresh} />

      <div className="card" style={{ marginTop: 20 }}>
        <h3 style={{ margin: "0 0 10px" }}>Active plans</h3>
        {grouped.active.length === 0 ? (
          <div className="muted">No plans yet — add one above. Until then, jobs are priced from the env rate card.</div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Model</th><th>Service</th><th>Label</th><th>Basis</th><th>Effective</th><th></th></tr>
            </thead>
            <tbody>{grouped.active.map((p) => <PlanRow key={p.id} p={p} onChanged={refresh} />)}</tbody>
          </table>
        )}

        {grouped.history.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>
              {grouped.history.length} retired plan(s)
            </summary>
            <table className="table" style={{ marginTop: 8 }}>
              <tbody>{grouped.history.map((p) => <PlanRow key={p.id} p={p} onChanged={refresh} />)}</tbody>
            </table>
          </details>
        )}
      </div>

      <FxRatesPanel enabled={enabled} onChanged={refresh} />
      <ElevenLabsBalanceCard enabled={enabled} />
    </div>
  );
}
